import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(24); // 24px = bottom-6
  const location = useLocation();

  // Scroll to hero section on route change or page reload
  useEffect(() => {
    const scrollToHero = () => {
      const heroElement = document.getElementById('hero');
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback to top if hero element not found
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(scrollToHero, 100);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Also scroll on initial page load/reload
  useEffect(() => {
    const scrollToHero = () => {
      const heroElement = document.getElementById('hero');
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback to top if hero element not found
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Scroll on initial mount (handles page reloads)
    const timeoutId = setTimeout(scrollToHero, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Show button when page is scrolled down and adjust position based on footer
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }

      // Check if footer is visible in viewport
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const footerVisible = footerRect.top < windowHeight && footerRect.bottom > 0;
        
        if (footerVisible) {
          // Footer is visible, adjust button position to sit above footer
          // Distance from bottom of viewport to top of footer
          const distanceFromBottom = windowHeight - footerRect.top;
          // Position button 24px above the footer
          setBottomOffset(distanceFromBottom + 24);
        } else {
          // Footer is not visible, use default position
          setBottomOffset(24);
        }
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    window.addEventListener('resize', toggleVisibility);
    toggleVisibility(); // Initial check
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
      window.removeEventListener('resize', toggleVisibility);
    };
  }, []);

  // Scroll to hero section smoothly
  const scrollToTop = () => {
    const heroElement = document.getElementById('hero');
    if (heroElement) {
      heroElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="fixed left-3 sm:left-6 z-40 w-12 h-12 sm:w-14 sm:h-14 bg-transparent hover:bg-transparent text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-110 flex items-center justify-center group backdrop-blur-lg animate-fadeInUp"
          style={{ bottom: `${bottomOffset}px` }}
          aria-label="Scroll to top"
        >
          <svg 
            className="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:-translate-y-1" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2.5} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        </button>
      )}
    </>
  );
};

export default ScrollToTop;
